import {
  PAPEL_DA_FONTE,
  TextBubble,
  fontFamilies,
} from "../classes/text-bubble.class";
import { Rectangle } from "../classes/rectangle.class";
import { Reticula, ROTULO_RETICULA } from "../api/documento.types";
import { proximaReticula } from "../classes/trama-do-quadro";
import { abrirEdicaoDeTexto } from "../classes/editar-texto-do-balao";
import { MuiColorInput } from "mui-color-input";

import BinIcon from "../assets/criar-comic/item-menu-icons/bin-icon.png";
import CopyIcon from "../assets/criar-comic/item-menu-icons/copy-icon.png";
import FlipIconIcon from "../assets/criar-comic/item-menu-icons/flip-icon.png";
import LayerUpIcon from "../assets/criar-comic/item-menu-icons/layer-up-icon.png";
import LayerDownIcon from "../assets/criar-comic/item-menu-icons/layer-down-icon.png";
import BoldIcon from "../assets/criar-comic/item-menu-icons/bold-icon.png";
import FilledBoldIcon from "../assets/criar-comic/item-menu-icons/filled-bold-icon.png";
import UnderlineIcon from "../assets/criar-comic/item-menu-icons/underline-icon.png";
import FilledUnderlineIcon from "../assets/criar-comic/item-menu-icons/filled-underline-icon.png";
import ItalicIcon from "../assets/criar-comic/item-menu-icons/italic-icon.png";
import FilledItalicIcon from "../assets/criar-comic/item-menu-icons/filled-italic-icon.png";
import BorderColorIcon from "../assets/criar-comic/item-menu-icons/border-color-icon.png";
import FillIcon from "../assets/criar-comic/item-menu-icons/fill-icon.png";
import EditTextIcon from "../assets/criar-comic/item-menu-icons/edit-text-icon.png";
import { RenderElProps } from "../classes/draggable-item.class";
import { rotuloDeMover } from "../classes/onde-na-pilha";
import { useState } from "react";

/**
 * O BOTÃO DO MENU DO ITEM — e ele existe porque estes controles eram `<img onClick>`.
 *
 * ── O QUE FOI MEDIDO EM 26/09/2026 ───────────────────────────────────────────────────────────
 *
 * Perguntando à árvore do DOM depois de selecionar um quadro na vitrine: o menu aparece, tem
 * **332×56**, e os onze controles dentro dele são **`<img>` de 44×44**. A régua de toque de 44 px
 * que o `T4.7` cobrava está cumprida — e nenhum deles é botão.
 *
 * ── POR QUE IMAGEM COM `onClick` NÃO É CONTROLE ──────────────────────────────────────────────
 *
 * Um `<img onClick>` não entra na ordem de Tab, não responde a Enter nem a Espaço, não tem estado
 * de foco visível e não se anuncia como controle para leitor de tela — ele se anuncia como
 * *imagem*. Quem navega por teclado não alcança o controle de camadas; quem usa leitor de tela
 * ouve "imagem, Remover" e não sabe que dá para acionar.
 *
 * **E esta é a terceira vez que este épico encontra a mesma doença**, com o mesmo nome escrito
 * duas vezes antes: *"era uma `<div className='button'>`: parecia botão, não era. Sem Tab, sem
 * Enter, sem estado de foco"* — nas ações do acervo e no "Adicionar página". Aqui são onze de uma
 * vez, no menu onde mora o controle que ele disse **quatro vezes** que não existia.
 *
 * ── O QUE ESTE COMPONENTE FAZ, E O QUE ELE NÃO MUDA ─────────────────────────────────────────
 *
 * Envolve o ícone num `<button type="button">`, com o rótulo no `title` **e** no `aria-label`, e a
 * imagem com `alt=""` — porque o botão já carrega o nome, e repetir faz o leitor de tela dizer
 * duas vezes. O tamanho continua vindo do ícone: 44×44, medido, não mexido.
 */
function BotaoDoMenu({
  icone,
  rotulo,
  aoClicar,
  pressionado,
  desativado,
}: {
  icone: string;
  rotulo: string;
  aoClicar: () => void;
  pressionado?: boolean;
  /**
   * T1.7 · DESATIVAR EM VEZ DE DEIXAR TENTAR E FALHAR, que e o que o T8.30 pede em geral e o
   * que a tela de publicar ja faz. Clicar em Mover para cima no item que ja esta na frente nao
   * fazia nada e nao dizia nada — e nao fazer nada em silencio e o que ensina que o controle
   * esta quebrado.
   */
  desativado?: boolean;
}) {
  return (
    <button
      type="button"
      className="item-menu-btn"
      title={rotulo}
      aria-label={rotulo}
      aria-pressed={pressionado}
      disabled={desativado}
      onClick={aoClicar}
    >
      <img draggable={false} src={icone} alt="" width={44} height={44} />
    </button>
  );
}

export function DeleteButton(props: RenderElProps) {
  return (
    <BotaoDoMenu
      icone={BinIcon}
      rotulo="Remover"
      aoClicar={() => {
        props.setPopUpInfo({
          type: "confirm",
          text: "Remover elemento?",
          close: () => props.setPopUpInfo(undefined),
          confirm: () => {
            props.comic.deleteItem(props.item);
            props.setPopUpInfo(undefined);
          },
        });
      }}
    />
  );
}

export function DuplicateElButton(props: RenderElProps) {
  return (
    <BotaoDoMenu
      icone={CopyIcon}
      rotulo="Duplicar"
      aoClicar={async () => {
        await props.comic.duplicateItem(props.item, {
          x: 20,
          y: 20,
          rotation: 0,
        });
      }}
    />
  );
}

export function MoveUpperButton(props: RenderElProps) {
  // T1.7 · O ROTULO DIZ ONDE O ITEM ESTA. A queixa e literal: nao da pra saber qual layer esta
  // ativa. A informacao sempre existiu na ordem do array; ela nunca foi dita em lugar nenhum da
  // tela — medido em 27/09 varrendo o innerText inteiro do editor com um item selecionado, e a
  // palavra camada nao aparecia uma vez.
  const lugar = props.comic.lugarNaPilha(props.item);
  return (
    <BotaoDoMenu
      icone={LayerUpIcon}
      rotulo={rotuloDeMover(lugar, "cima")}
      desativado={!lugar.podeSubir}
      aoClicar={() => {
        props.comic.moveEl(props.item, "up");
      }}
    />
  );
}
export function MoveLowerButton(props: RenderElProps) {
  return (
    <BotaoDoMenu
    // Dizia "Mover para cima", copiado do botao de cima. Os dois botoes de camada tinham o
    // MESMO nome acessivel: quem navega por leitor de tela ouvia "mover para cima" duas vezes e
    // nao tinha como saber qual descia. O T4.7 e a queixa de que camada "nao existe" — e um
    // controle que se anuncia errado e um controle que nao da para usar.
      icone={LayerDownIcon}
      rotulo={rotuloDeMover(props.comic.lugarNaPilha(props.item), "baixo")}
      desativado={!props.comic.lugarNaPilha(props.item).podeDescer}
      aoClicar={() => {
        props.comic.moveEl(props.item, "down");
      }}
    />
  );
}

export function MirrorButton(props: RenderElProps) {
  return (
    <BotaoDoMenu
      icone={FlipIconIcon}
      rotulo="Espelhar"
      aoClicar={() => {
        props.item.mirrorEl();
      }}
    />
  );
}

export function ToggleBoldButton(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;
  return (
    <BotaoDoMenu
      icone={item.bold ? FilledBoldIcon : BoldIcon}
      rotulo="Negrito"
      pressionado={item.bold}
      aoClicar={() => {
        item.toggleBold();
      }}
    />
  );
}
export function ToggleUnderlineButton(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;
  return (
    <BotaoDoMenu
      icone={item.underline ? FilledUnderlineIcon : UnderlineIcon}
      rotulo="Sublinhado"
      pressionado={item.underline}
      aoClicar={() => {
        item.toggleUnderline();
      }}
    />
  );
}
export function ToggleItalicButton(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;
  return (
    <BotaoDoMenu
      icone={item.italic ? FilledItalicIcon : ItalicIcon}
      rotulo="Italico"
      pressionado={item.italic}
      aoClicar={() => {
        item.toggleItalic();
      }}
    />
  );
}

export function BorderColorButton(props: RenderElProps) {
  const item = props.item as Rectangle;

  const [isEditingColor, setIsEditingColor] = useState(false);
  const [color, setColor] = useState(item.borderColor);

  const validClasses = [Rectangle];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  // const editingColor = props.EditingColor();
  return (
    <>
      {isEditingColor && (
        <div style={{ position: "absolute" }}>
          <MuiColorInput
            style={{
              // zIndex: 100,
              // position: "absolute",
              backgroundColor: "white",
              // left: colorSelectorInfo.boundingRect.x,
              // top: colorSelectorInfo.boundingRect.y,
            }}
            format="hex"
            value={color}
            onChange={(value) => {
              item.setBorderColor(value);
              setColor(value);
            }}
            contentEditable={false}
            InputProps={{ readOnly: true }}
          />
        </div>
      )}

      <BotaoDoMenu
      icone={BorderColorIcon}
      rotulo="Cor da borda"
      aoClicar={() => {
          setIsEditingColor(!isEditingColor);
        }}
    />
    </>
  );
}
export function FillColorButton(props: RenderElProps) {
  const item = props.item as Rectangle;

  const [isEditingColor, setIsEditingColor] = useState(false);
  const [color, setColor] = useState(item.fillColor);

  const validClasses = [Rectangle];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  return (
    <>
      {isEditingColor && (
        <div style={{ position: "absolute" }}>
          <MuiColorInput
            style={{
              // zIndex: 100,
              // position: "absolute",
              backgroundColor: "white",
              // left: colorSelectorInfo.boundingRect.x,
              // top: colorSelectorInfo.boundingRect.y,
            }}
            format="hex"
            value={color}
            onChange={(value) => {
              item.setFillColor(value);
              setColor(value);
            }}
            contentEditable={false}
            InputProps={{ readOnly: true }}
          />
        </div>
      )}

      <BotaoDoMenu
      icone={FillIcon}
      rotulo="Cor do preenchimento"
      aoClicar={() => {
          setIsEditingColor(!isEditingColor);
        }}
    />
    </>
  );
}

export function FontSizeSelect(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;
  return (
    <select
      // T2.106 · ERA 40 px DE LARGURA, e a regua e 44. Medido na varredura do editor com um item
      // selecionado: 40x44 — quatro pixels, e ainda assim e o unico controle do menu do item que
      // nao passava. Quatro pixels sao de graca aqui, e o numero de duas casas continua caber.
      style={{ width: "44px" }}
      value={item.FontSize()}
      onChange={(e) => {
        item.FontSize(Number(e.target.value));
      }}
    >
      {new Array(50)
        .fill(null)
        .map((_, i) => i + 1)
        .map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
    </select>
  );
}
export function FontFamilySelect(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;

  // ── ESTA LISTA ESTAVA DUPLICADA, E A COPIA ERRADA ERA A QUE A TELA USAVA ────────────────
  //
  // `text-bubble.class.tsx` EXPORTA `fontFamilies`, e este arquivo tinha a propria copia, escrita
  // a mao, com as mesmas seis familias. A exportada nao era consumida por ninguem.
  //
  // Isso significa que trocar a lista no lugar obvio — junto da classe que usa a fonte — nao
  // mudaria nada na tela. E o tipo de duplicacao que faz um conserto parecer feito e nao estar:
  // a mesma licao que o rodape do XequeMath ja tinha pago ("rodape copiado e rodape que
  // diverge"), e que este ticket viu de novo hoje no `.mq-abrir`.
  //
  // Agora ha uma fonte so, e ela vem do manual de marca (T2.96). Ver o comentario dela.
  return (
    <select
      // Mais largo que os 90px de antes: os rotulos agora dizem o PAPEL da fonte, e nao o nome
      // tecnico da familia. "Fala (a letra do HQ-Lab)" nao cabe em 90px, e o nome tecnico nao
      // significa nada para quem tem oito anos.
      style={{ width: "170px" }}
      value={item.FontFamily()}
      onChange={(e) => {
        item.FontFamily(e.target.value);
      }}
    >
      {fontFamilies.map((fontFamily) => (
        <option key={fontFamily} value={fontFamily} style={{ fontFamily }}>
          {PAPEL_DA_FONTE[fontFamily] ?? fontFamily}
        </option>
      ))}
    </select>
  );
}
export function EditTextButton(props: RenderElProps) {
  const validClasses = [TextBubble];
  if (!validClasses.some((c) => props.item instanceof c)) return <></>;

  const item = props.item as TextBubble;
  return (
    <BotaoDoMenu
      icone={EditTextIcon}
      rotulo="Editar texto"
      // T8.10 · a mesma caixa que o clique duplo no balao abre. O corpo dela saiu daqui para
      // `editar-texto-do-balao.ts` no dia em que passou a ter dois gestos: duas copias divergem no
      // primeiro dia em que alguem muda uma, e aqui as duas parecem funcionar.
      aoClicar={() => abrirEdicaoDeTexto(item, props.setPopUpInfo)}
    />
  );
}

/**
 * A RETÍCULA DO QUADRO — `T2.108`, e é o único ponto dos 109 em que ele diz *essencial*.
 *
 * *"uma feature para aplicação de retícula EM QUALQUER UM DOS CAMPOS que a gente vai ter"*. A
 * metade de "todas as páginas" já existia; esta é a do campo.
 *
 * ── POR QUE UM BOTÃO QUE CICLA, E NÃO UM SELETOR ─────────────────────────────────────────────
 *
 * São quatro valores e o menu do item é uma fila de ícones — um `<select>` ali dentro abriria
 * uma lista sobre a folha, que é o que a criança está olhando. Ciclar é o gesto que o próprio
 * menu já ensina (espelhar também é um botão que alterna), e o `title` diz onde se está e o que
 * vem a seguir, para ninguém precisar clicar quatro vezes para descobrir.
 *
 * O ícone é SVG desenhado aqui, e não PNG: ele MOSTRA a intensidade atual — três pontos, cinco
 * pontos, nove pontos —, e um raster teria que ser quatro arquivos.
 */
export function ReticulaButton(props: RenderElProps) {
  const item = props.item as Rectangle;
  if (!(item instanceof Rectangle)) return <></>;

  const atual: Reticula = item.reticula ?? "nenhuma";
  const proxima = proximaReticula(atual);
  // Quantos pontos o ícone desenha em cada intensidade. "nenhuma" desenha a moldura vazia, para
  // o botão continuar sendo reconhecível como o botão da trama.
  const pontosPorIntensidade: Record<Reticula, number[][]> = {
    nenhuma: [],
    leve: [[8, 8], [16, 16]],
    media: [[6, 6], [12, 12], [18, 18], [18, 6], [6, 18]],
    forte: [
      [5, 5], [12, 5], [19, 5],
      [5, 12], [12, 12], [19, 12],
      [5, 19], [12, 19], [19, 19],
    ],
  };

  // T4.7 · ERA UM `<svg onClick>`, e a troca de hoje pegou os onze `<img>` e DEIXOU ESTE.
  // Um SVG com `role="img"` se anuncia com nome — melhor que imagem nua — e continua fora da ordem
  // de Tab e sem responder a Enter. O botão de verdade fica por fora, e o SVG passa a ser só o
  // desenho: `aria-hidden`, porque o botão já carrega o nome.
  return (
    <button
      type="button"
      className="item-menu-btn"
      title={`Retícula: ${ROTULO_RETICULA[atual]} — tocar para ${ROTULO_RETICULA[proxima]}`}
      aria-label={`Retícula: ${ROTULO_RETICULA[atual]}. Tocar para ${ROTULO_RETICULA[proxima]}`}
      onClick={() => {
        item.setReticula(proxima);
        // A trama vive no DADO do item e precisa de uma `Image` nova, que carrega de forma
        // assíncrona — então não dá para falar com o nó do Konva direto, como fazem as cores.
        // `redesenharItem` reemite os itens da página e registra a alteração para o desfazer.
        props.comic.redesenharItem(item);
      }}
    >
      <svg viewBox="0 0 24 24" width={44} height={44} aria-hidden="true" focusable="false">
      <rect
        x="2.5"
        y="2.5"
        width="19"
        height="19"
        rx="3"
        fill="#fffbe0"
        stroke="#1b1b1b"
        strokeWidth="1.6"
      />
      {pontosPorIntensidade[atual].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="1.6" fill="#1b1b1b" />
      ))}
    </svg>
    </button>
  );
}
